import { Box, Flex, Text } from '@chakra-ui/react';
import { Tooltip } from '@chakra-ui/tooltip';
import { faFileImport } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonText, Modal } from '@morressier/alembic';
import { ITransferHistoryItem } from '@publishing-engine/domain/models';
import { useState } from 'react';
import { formatDate } from 'src/utils/date';

interface Props {
  items: ITransferHistoryItem[];
}

const renderTooltipContent = (item: ITransferHistoryItem) => {
  switch (item.type) {
    case 'accept':
      return (
        <Flex flexDirection={'column'} gap={2}>
          <Text>
            Rejected from {item.fromSubmissionContextId} -&gt; Transferred to{' '}
            {item.toSubmissionContextId}
          </Text>
          <Text>
            Click on the button to see this submission’s complete transfer
            history.
          </Text>
        </Flex>
      );
    case 'offer':
      return (
        <Flex flexDirection={'column'} gap={2}>
          <Text>
            Awaiting Author Decision: the author can either accept one of the
            transfer offers or reject them.
          </Text>
          <Text>
            Click on the button to see the current transfer choices offered to
            the author.
          </Text>
        </Flex>
      );
    case 'reject':
      return (
        <Flex flexDirection={'column'} gap={2}>
          <Text>
            Rejected from {item.fromSubmissionContextId}: the author decided to
            not transfer their submission.
          </Text>
          <Text>
            Click on the button to see this submission’s complete transfer
            history.
          </Text>
        </Flex>
      );
  }
};

const TransferItemInfo = ({ item }: { item: ITransferHistoryItem }) => {
  let content: React.ReactNode = null;

  switch (item.type) {
    case 'accept':
      content = (
        <Text textStyle="headingItem">
          Rejected from {item.fromSubmissionContextId} -&gt; Transferred to{' '}
          {item.toSubmissionContextId}
        </Text>
      );
      break;
    case 'offer':
      content = (
        <>
          <Text textStyle="headingItem">Awaiting Author Decision</Text>
          <Text>The author was given the following transfer options:</Text>
          <Box paddingStart={2}>
            <ul>
              {item.targets.map(journal => (
                <li key={journal.submissionContextId}>
                  <Text textStyle="textBody">
                    {journal.submissionContextId}
                  </Text>
                </li>
              ))}
            </ul>
          </Box>
        </>
      );
      break;
    case 'reject':
      content = (
        <>
          <Text textStyle="headingItem">
            Rejected from {item.fromSubmissionContextId}
          </Text>
          <Text>The author decided to not transfer their submission.</Text>
        </>
      );
      break;
  }

  return (
    <Flex flexDirection={'column'} gap={2}>
      <Text textStyle="textContext" textTransform="uppercase">
        {formatDate(new Date(item.date))}
      </Text>
      {content}
    </Flex>
  );
};

export const AuthorSubmissionTransferHistory: React.FC<Props> = ({ items }) => {
  const [showModal, setShowModal] = useState(false);
  const latest = items[0];

  if (!latest) return null;

  const onClose = () => setShowModal(false);
  return (
    <>
      <Tooltip
        hasArrow
        shouldWrapChildren
        placement="top"
        label={renderTooltipContent(latest)}
        borderRadius={4}
      >
        <ButtonText maxWidth="fit-content" onClick={() => setShowModal(true)}>
          View Transfer Info
        </ButtonText>
      </Tooltip>

      <Modal
        isOpen={showModal}
        closeButtonProps={{
          hidden: true
        }}
        onClose={onClose}
        width="720px"
      >
        <Flex
          padding={8}
          gap={6}
          flexDirection="column"
          maxHeight="80vh"
          overflow="auto"
        >
          <Box>
            <FontAwesomeIcon fontSize={32} icon={faFileImport} />
          </Box>

          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingGroup">Transfer Info</Text>
          </Flex>

          {items.map(item => (
            <TransferItemInfo key={item.id} item={item} />
          ))}

          <Flex
            gap={2}
            justifyContent="flex-end"
            alignItems="center"
            alignSelf="stretch"
          >
            <Button
              onClick={onClose}
              variant="primary"
              type="submit"
              borderRadius="small"
            >
              Close
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
